import { useState, useEffect } from 'react';
import { Check, Xmark, Sparks } from 'iconoir-react';
import { fetchUserApps, grantAppAccess, revokeAppAccess, grantAllApps } from '../../lib/user-apps-api';

const ALL_APPS = [
  { key: 'admin', name: 'ThiranAI', icon: '🧠', color: '#13afe3' },
  { key: 'iam', name: 'IAM Service', icon: '🛡️', color: '#8b5cf6' },
  { key: 'training', name: 'LLM Training', icon: '🎓', color: '#059669' },
  { key: 'demo', name: 'Demo & Maximo', icon: '📊', color: '#ec4899' },
  { key: 'experimental', name: 'Experimental', icon: '🧪', color: '#f59e0b' },
  { key: 'finetuning', name: 'Finetuning Studio', icon: '🔧', color: '#f59e0b' },
];

interface UserAppsModalProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

export function UserAppsModal({ userId, userName, onClose }: UserAppsModalProps) {
  const [userApps, setUserApps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadUserApps();
  }, [userId]);

  const loadUserApps = async () => {
    setLoading(true);
    try {
      const apps = await fetchUserApps(userId);
      setUserApps(apps);
    } catch (error) {
      console.error('Failed to load user apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApp = async (appKey: string) => {
    setUpdating(true);
    try {
      const hasAccess = userApps.includes(appKey);
      if (hasAccess) {
        await revokeAppAccess(userId, appKey);
        setUserApps(userApps.filter(k => k !== appKey));
      } else {
        await grantAppAccess(userId, appKey);
        setUserApps([...userApps, appKey]);
      }
    } catch (error) {
      console.error('Failed to toggle app access:', error);
      alert('Failed to update app access');
    } finally {
      setUpdating(false);
    }
  };

  const handleGrantAll = async () => {
    setUpdating(true);
    try {
      await grantAllApps(userId);
      setUserApps(ALL_APPS.map(app => app.key));
    } catch (error) {
      console.error('Failed to grant all apps:', error);
      alert('Failed to grant all apps');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1f2937' }}>
            App Permissions
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
            Manage app access for <strong>{userName}</strong>
          </p>
        </div>

        {/* Grant All Button */}
        <button
          onClick={handleGrantAll}
          disabled={updating}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #13afe3, #6366f1)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: updating ? 'not-allowed' : 'pointer',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: updating ? 0.6 : 1,
          }}
        >
          <Sparks width={16} height={16} />
          Grant All Apps
        </button>

        {/* Apps Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
            Loading...
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {ALL_APPS.map((app) => {
              const hasAccess = userApps.includes(app.key);
              return (
                <button
                  key={app.key}
                  onClick={() => handleToggleApp(app.key)}
                  disabled={updating}
                  style={{
                    padding: '16px',
                    background: hasAccess ? app.color : '#f9fafb',
                    color: hasAccess ? '#fff' : '#1f2937',
                    border: hasAccess ? 'none' : '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: updating ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: updating ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontSize: '28px' }}>{app.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: 600 }}>{app.name}</div>
                  <div style={{ fontSize: '16px' }}>
                    {hasAccess ? (
                      <Check width={20} height={20} style={{ strokeWidth: 3 }} />
                    ) : (
                      <Xmark width={20} height={20} style={{ strokeWidth: 2, opacity: 0.4 }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            marginTop: '24px',
            width: '100%',
            padding: '12px',
            background: '#f3f4f6',
            color: '#1f2937',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
